import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

export type MfaPurpose = 'LOGIN' | 'SIGNUP' | 'PASSWORD_RESET' | 'INVITATION_SETUP' | 'SENSITIVE-ACTION';
export type MfaChannel = 'EMAIL' | 'SMS' | 'AUTHENTICATOR_APP' | 'BACKUP_CODE';
export type SignupRole = 'landlord' | 'property_manager';

export const MFA_SESSION_TTL_SECONDS = 15 * 60;
// Must outlive the MFA code (and the invitation page before it), or an invitation setup that takes
// more than a few minutes loses its return URL and the invitation is never accepted.
export const RETURN_URL_TTL_SECONDS = 30 * 60;

export interface FlowCtx {
  purpose: MfaPurpose;
  challengeId?: string | null;
  preAuthToken?: string | null;
  userId?: string | null;
  emailMasked?: string | null;
  channel?: MfaChannel;
  authenticatorConfigured?: boolean;
  workspaceOrgId?: string | null;
  workspaceOrgName?: string | null;
  workspacePortalSlug?: string | null;
  returnApp?: 'tenant' | 'maintenance' | null;
  rememberMe?: boolean;
  createdAt: number;
  expSec: number;
}


export interface MfaProof {
  purpose: MfaPurpose;
  challengeId?: string | null;
  userId?: string | null;
  channel?: MfaChannel | null;
  verifiedAt: number;
  expSec: number;
}

export interface AuthAppSetupState {
  manualEntryKey: string;
  qrCodeImageBase64: string;
}

const NS = 'apx:v1:';

const K = {
  mfa: `${NS}mfaCtx`,
  ret: `${NS}returnUrl`,
  proof: `${NS}mfaProof`,
  signupRole: `${NS}signupRole`,
  authAppSetup: `${NS}authAppSetup`,
};

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly storage: Storage | null;
  private storageSub?: Subscription;

  private readonly flowSubj = new BehaviorSubject<FlowCtx | null>(null);
  readonly flow$ = this.flowSubj.asObservable();

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    const browser = isPlatformBrowser(platformId);
    this.storage = browser ? window.sessionStorage : null;

    if (browser && this.storage) {
      this.flowSubj.next(this.read<FlowCtx>(K.mfa));

      this.storageSub = fromEvent<StorageEvent>(window, 'storage').subscribe((event) => {
        if (event.key === K.mfa) {
          this.flowSubj.next(this.read<FlowCtx>(K.mfa));
        }
      });
    }
  }

  setMfaCtx(ctx: Partial<FlowCtx> & { purpose: MfaPurpose }): void {
    const toStore: FlowCtx = {
      purpose: ctx.purpose,
      challengeId: ctx.challengeId ?? null,
      preAuthToken: ctx.preAuthToken ?? null,
      userId: ctx.userId ?? null,
      emailMasked: ctx.emailMasked ?? null,
      channel: ctx.channel,
      authenticatorConfigured: ctx.authenticatorConfigured,
      workspaceOrgId: ctx.workspaceOrgId ?? null,
      workspaceOrgName: ctx.workspaceOrgName ?? null,
      workspacePortalSlug: ctx.workspacePortalSlug ?? null,
      returnApp: ctx.returnApp === 'tenant' || ctx.returnApp === 'maintenance' ? ctx.returnApp : null,
      rememberMe: ctx.rememberMe ?? false,
      createdAt: ctx.createdAt ?? Date.now(),
      expSec: ctx.expSec ?? MFA_SESSION_TTL_SECONDS,
    };

    this.write(K.mfa, toStore);
    this.flowSubj.next(toStore);
  }

  patchMfaCtx(patch: Partial<FlowCtx>): void {
    const current = this.read<FlowCtx>(K.mfa);
    if (!current) {
      return;
    }

    this.setMfaCtx({
      ...current,
      ...patch,
      purpose: current.purpose,
      expSec: patch.expSec ?? current.expSec,
    });
  }

  getFlow(): FlowCtx | null {
    const flow = this.read<FlowCtx>(K.mfa);
    if (!flow) {
      return null;
    }

    const ageSec = (Date.now() - flow.createdAt) / 1000;
    if (flow.expSec && ageSec > flow.expSec) {
      this.clearFlow();
      return null;
    }

    return flow;
  }

  clearFlow(): void {
    this.remove(K.mfa);
    this.flowSubj.next(null);
  }

  setMfaProof(proof: Omit<MfaProof, 'verifiedAt'>): void {
    const finalProof: MfaProof = {
      ...proof,
      verifiedAt: Date.now(),
      expSec: proof.expSec ?? 300,
    };
    this.write(K.proof, finalProof);
  }

  getMfaProof(): MfaProof | null {
    const proof = this.read<MfaProof>(K.proof);
    if (!proof) {
      return null;
    }

    const ageSec = (Date.now() - proof.verifiedAt) / 1000;
    if (proof.expSec && ageSec > proof.expSec) {
      this.clearMfaProof();
      return null;
    }

    return proof;
  }

  clearMfaProof(): void {
    this.remove(K.proof);
  }

  ensureSafeReturnUrl(url: string | null | undefined): string | null {
    if (!url) {
      return null;
    }

    if (!/^\/(?!\/)/.test(url)) {
      return null;
    }

    try {
      const origin =
        typeof window !== 'undefined' && window.location
          ? window.location.origin
          : 'http://localhost';

      const parsed = new URL(origin + url);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  }

  setReturnUrl(url: string): void {
    const safe = this.ensureSafeReturnUrl(url);
    if (!safe) {
      return;
    }

    this.write(K.ret, {
      url: safe,
      createdAt: Date.now(),
    });
  }

  getReturnUrl(): string | null {
    const stored = this.read<{ url: string; createdAt: number }>(K.ret);
    if (!stored) {
      return null;
    }

    const ageSec = (Date.now() - stored.createdAt) / 1000;
    if (ageSec > RETURN_URL_TTL_SECONDS) {
      this.remove(K.ret);
      return null;
    }

    return stored.url;
  }

  popReturnUrl(): string | null {
    const stored = this.read<{ url: string; createdAt: number }>(K.ret);
    this.remove(K.ret);

    if (!stored) {
      return null;
    }

    const ageSec = (Date.now() - stored.createdAt) / 1000;
    if (ageSec > RETURN_URL_TTL_SECONDS) {
      return null;
    }

    return stored.url;
  }

  setSignupRole(role: SignupRole): void {
    this.write(K.signupRole, role);
  }

  getSignupRole(): SignupRole | null {
    const value = this.read<SignupRole>(K.signupRole);
    if (value === 'landlord' || value === 'property_manager') {
      return value;
    }
    return null;
  }

  clearSignupRole(): void {
    this.remove(K.signupRole);
  }

  clearAll(): void {
    this.clearFlow();
    this.clearMfaProof();
    this.remove(K.ret);
    this.clearSignupRole();
    this.clearAuthAppSetup();
  }

  private read<T>(key: string): T | null {
    const raw = this.storage?.getItem(key) ?? null;
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  setAuthAppSetup(state: AuthAppSetupState): void {
    this.write(K.authAppSetup, state);
  }

  getAuthAppSetup(): AuthAppSetupState | null {
    return this.read<AuthAppSetupState>(K.authAppSetup);
  }

  clearAuthAppSetup(): void {
    this.remove(K.authAppSetup);
  }

  private write(key: string, value: unknown): void {
    this.storage?.setItem(key, JSON.stringify(value));
  }

  private remove(key: string): void {
    this.storage?.removeItem(key);
  }
}
